<script setup>
import axios from 'axios';
import { ref, computed, watch } from 'vue';
import { Head, router, useForm, usePage } from '@inertiajs/vue3';
import { PhStudent, PhCamera } from "@phosphor-icons/vue";
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Table from '@pages/Shared/ui/Table.vue';
import Spinner from '@pages/Shared/ui/Spinner.vue';
import Modal from '@pages/Shared/Modal.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Input from '@pages/Shared/form/Input.vue';
import Select from '@pages/Shared/form/Select.vue';
import Textarea from '@pages/Shared/form/Textarea.vue';
import { flashToast } from "@/toast.js";
import userMale from '@/../images/user-male.png';
import userFemale from '@/../images/user-female.png';
import { capitalize } from '@/helpers.js';

const props = defineProps({
    students: Object,
    sections: Array,
    status:   Array,
    filters:  Object,
});

const openModal   = ref(false);
const modalStatus = ref(null);
const modalTitle  = ref('');
const showPrompt  = ref(false);
const fileInput   = ref(null);
const yearOfAdmission = ref(new Date().getFullYear());

const filters = ref({ search: null, section: null, class: null, status: null, ...props.filters });

const form = useForm({
    _method:        null,
    id:             null,
    avatar:         null,
    preview:        null,
    first_name:     '',
    last_name:      '',
    other_name:     '',
    email:          '',
    gender:         null,
    dob:            '',
    phone:          '',
    address:        '',
    guardian:       '',
    guardian_phone: '',
    status:         null,
    class:          null,
    section:        null,
});

const columns = [
    { key: 'index',                  label: '#',             type: 'index'   },
    { key: 'admission',              label: 'Student ID',    type: 'slot'    },
    { key: 'avatar',                 label: 'Avatar',        type: 'slot'    },
    { key: 'name',                   label: 'Name',          type: 'slot'    },
    { key: 'email',                  label: 'Email'                          },
    { key: 'gender',                 label: 'Gender'                         },
    { key: 'dob',                    label: 'DOB'                            },
    { key: 'phone',                  label: 'Phone'                          },
    { key: 'address',                label: 'Address'                        },
    { key: 'guardian',               label: 'Guardian'                       },
    { key: 'guardian_phone',         label: 'Guardian Phone'                 },
    { key: 'class_room.name',        label: 'Class'                          },
    { key: 'class_room.section.name',label: 'Section'                        },
    { key: 'status',                 label: 'Status',        type: 'slot'    },
    { key: 'actions',                label: '',              type: 'actions' },
];

const setYears = () => {
    const years = [];
    for (let year = new Date().getFullYear(); year >= 2012; year--) years.push(year);
    return years;
};

const selectedClasses = computed(() => {
    if (!filters.value?.section) return [];
    const section = props.sections.find(s => String(s.id) === String(filters.value.section));
    return section?.classes || [];
});

const modalClasses = computed(() => {
    if (!form.section) return [];
    const section = props.sections.find(s => String(s.id) === String(form.section));
    return section?.classes || [];
});

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true });

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    );
    router.get('/admin/students', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const handleSearch = (value) => { filters.value.search = value; applyFilters(); };

const handleFilter = (newFilters) => {
    const sectionChanged = Object.prototype.hasOwnProperty.call(newFilters, 'section') &&
                           newFilters.section !== filters.value.section;
    filters.value = { ...filters.value, ...newFilters };
    if (sectionChanged) filters.value.class = null;
    applyFilters();
};

const setFormData = (student) => {
    form.id             = student.id;
    form.avatar         = student.avatar;
    form.first_name     = student.first_name;
    form.last_name      = student.last_name;
    form.other_name     = student.other_name;
    form.email          = student.email;
    form.gender         = student.gender;
    form.dob            = student.dob;
    form.phone          = student.phone;
    form.address        = student.address;
    form.guardian       = student.guardian;
    form.guardian_phone = student.guardian_phone;
    form.class          = student.class_room?.id ?? null;
    form.section        = student.class_room?.section?.id ?? null;
    form.status         = student.status;
    form.preview        = null;
};

const editStudent = (id) => {
    const student = props.students.data.find(s => s.id === id);
    if (!student) return;
    setFormData(student);
    modalTitle.value  = 'Edit Student';
    modalStatus.value = 'edit';
    openModal.value   = true;
};

const viewStudent = (id) => {
    const student = props.students.data.find(s => s.id === id);
    if (!student) return;
    setFormData(student);
    modalTitle.value  = 'View Student';
    modalStatus.value = 'view';
    openModal.value   = true;
};

const deleteStudent = (id) => { form.id = id; showPrompt.value = true; };

const confirmDelete = () => {
    form.delete(`/admin/students/${form.id}`, {
        onSuccess: (page) => {
            showPrompt.value = false;
            form.clearErrors();
            form.reset();
            flashToast(page.props.flash);
        }
    });
};

const openAddStudentModal = async () => {
    form.reset();
    form.clearErrors();
    modalTitle.value  = 'Add Student';
    modalStatus.value = 'create';
    openModal.value   = true;
};

const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.preview) URL.revokeObjectURL(form.preview);
    form.avatar  = file;
    form.preview = URL.createObjectURL(file);
};

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            if (form.preview) URL.revokeObjectURL(form.preview);
            form.clearErrors();
            form.reset();
            openModal.value = false;
            flashToast(page.props.flash);
        }
    };
    if (modalStatus.value === 'edit') {
        form._method = 'put';
        form.post(`/admin/students/${form.id}`, options);
    } else {
        form.post('/admin/students', options);
    }
};

const handleModalClose = () => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    modalStatus.value = null;
    modalTitle.value  = null;
    openModal.value   = false;
    form.clearErrors();
    form.reset();
};

// Watch section + year to auto-generate admission number on create
watch([() => form.section, yearOfAdmission], async ([sectionId, year]) => {
    if (!sectionId || !year) return;
    if (modalStatus.value === 'edit' || modalStatus.value === 'view') return;
    try {
        const res = await axios.post('/admin/students/admission', { section: sectionId, year });
        if (res.data?.admission) form.id = res.data.admission;
    } catch (e) {
        console.error('Admission fetch failed:', e);
    }
});

const page = usePage();
const can  = computed(() => page.props.can || {});
</script>

<template>
    <Layout>
        <Head title="Students Management" />

        <Prompt
            title="student"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

                <!-- Avatar -->
                <div class="relative w-28 h-28 mx-auto my-4">
                    <img
                        :src="form.preview
                            ? form.preview
                            : (form.avatar
                                ? `/storage/${form.avatar}`
                                : (form.gender === 'Male' ? userMale : userFemale))"
                        class="w-full h-full object-cover rounded-full ring-4 ring-gray-100"
                        alt="Student avatar"
                    />
                    <span
                        v-if="modalStatus !== 'view'"
                        class="absolute bottom-0 right-0 p-1.5 rounded-full text-white cursor-pointer transition"
                        :class="modalStatus === 'edit'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-primary hover:bg-primary-light'"
                        @click="fileInput?.click()"
                    >
                        <PhCamera :size="16" />
                    </span>
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        class="hidden"
                        @change="handleFileSelected"
                    />
                    <span v-if="form.errors.avatar" class="text-red-600 text-xs absolute -bottom-5">
                        {{ form.errors.avatar }}
                    </span>
                </div>

                <Input v-model="form.first_name" label="First Name" placeholder="First Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.first_name" class="mb-4" />

                <Input v-model="form.last_name" label="Last Name" placeholder="Last Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.last_name" class="mb-4" />

                <Input v-model="form.other_name" label="Other Name" placeholder="Other Name (Optional)"
                    :disabled="modalStatus === 'view'" :error="form.errors.other_name" class="mb-4" />

                <Input v-model="form.email" type="email" label="Email" placeholder="Email Address"
                    :disabled="modalStatus === 'view'" :error="form.errors.email" class="mb-4" />

                <Select v-model="form.gender" label="Gender" :options="['Male', 'Female']"
                    placeholder="Select Gender" :disabled="modalStatus === 'view'"
                    :error="form.errors.gender" class="mb-4" />

                <Input v-model="form.dob" type="date" label="Date of Birth"
                    :disabled="modalStatus === 'view'" :error="form.errors.dob" class="mb-4" />

                <Input v-model="form.phone" type="text" label="Phone" placeholder="Phone Number"
                    :disabled="modalStatus === 'view'" :error="form.errors.phone" class="mb-4" />

                <Textarea v-model="form.address" label="Address" placeholder="Enter address..."
                    :rows="2" :disabled="modalStatus === 'view'"
                    :error="form.errors.address" class="mb-4" />

                <Input v-model="form.guardian" label="Guardian" placeholder="Guardian Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.guardian" class="mb-4" />

                <Input v-model="form.guardian_phone" label="Guardian Phone" placeholder="Guardian Phone"
                    :disabled="modalStatus === 'view'" :error="form.errors.guardian_phone" class="mb-4" />

                <Select v-if="modalStatus === 'create'" v-model="yearOfAdmission"
                    label="Year of Admission" :options="setYears()"
                    placeholder="Select year" class="mb-4" />

                <Select v-model="form.section" label="Section"
                    :options="sections.map(s => ({ label: s.name, value: s.id }))"
                    placeholder="Select section" :disabled="modalStatus === 'view'"
                    :error="form.errors.section" class="mb-4" />

                <Select v-model="form.class" label="Class"
                    :options="modalClasses.map(c => ({ label: c.name, value: c.id }))"
                    placeholder="Select class" :disabled="modalStatus === 'view' || !form.section"
                    :error="form.errors.class" class="mb-4" />

                <Input v-model="form.id" type="text" label="Admission Number"
                    placeholder="Auto Generated" :disabled="true"
                    :error="form.errors.id" class="mb-4" />

                <Select v-if="modalStatus === 'edit' || modalStatus === 'view'"
                    v-model="form.status" label="Status"
                    :options="status.map(s => ({ label: capitalize(s.name), value: s.value }))"
                    placeholder="Select status" :disabled="modalStatus === 'view'"
                    :error="form.errors.status" class="mb-4" />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                           transition flex justify-center items-center gap-2"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Student' : 'Add Student' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Students</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all students</p>
            </div>
            <button
                v-if="can.createStudent"
                @click="openAddStudentModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhStudent :size="20" />
                Add Student
            </button>
        </div>

        <Table
            :data="students"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: sections.map(s => ({ label: s.name, value: s.id })),
                    value: filters.section,
                },
                {
                    key: 'class', label: 'Class',
                    options: selectedClasses.map(c => ({ label: c.name, value: c.id })),
                    value: filters.class,
                    disabled: !filters.section,
                },
                {
                    key: 'status', label: 'Status',
                    options: status.map(s => ({ label: capitalize(s.name), value: s.value })),
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="viewStudent"
            @edit="editStudent"
            @delete="deleteStudent"
        >
            <template #admission="{ row }">
                <span class="whitespace-nowrap text-sm text-gray-700">{{ row.id }}</span>
            </template>

            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar
                        ? `/storage/${row.avatar}`
                        : (row.gender === 'Male' ? userMale : userFemale)"
                    :alt="`${row.first_name} ${row.last_name}`"
                />
            </template>

            <template #name="{ row }">
                <span class="whitespace-nowrap text-sm font-medium text-gray-800">
                    {{ row.first_name }} {{ row.last_name }}
                    <span v-if="row.other_name" class="text-gray-800">{{ row.other_name }}</span>
                </span>
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="
                        row.status?.toLowerCase() === 'active'    ? 'bg-green-50 text-green-700'   :
                        row.status?.toLowerCase() === 'inactive'  ? 'bg-orange-50 text-orange-600' :
                        row.status?.toLowerCase() === 'graduated' ? 'bg-primary-50 text-primary-700'     :
                        'bg-red-50 text-red-600'
                    "
                >
                    <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="
                            row.status?.toLowerCase() === 'active'    ? 'bg-green-500'  :
                            row.status?.toLowerCase() === 'inactive'  ? 'bg-orange-400' :
                            row.status?.toLowerCase() === 'graduated' ? 'bg-primary-500'   :
                            'bg-red-500'
                        "
                    ></span>
                    {{ capitalize(row.status) }}
                </span>
            </template>
        </Table>
    </Layout>
</template>